import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft,
  Shield,
  CheckCircle,
  AlertTriangle,
  Upload,
  Fingerprint,
  Link2,
  Store,
  Search,
  Loader2,
  Copy,
  ExternalLink,
  Info,
  Camera
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SelleVerify() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  const [activeTab, setActiveTab] = useState("verify");
  const [verifying, setVerifying] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  // Verification inputs
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [productName, setProductName] = useState("");
  const [productDescription, setProductDescription] = useState("");

  // Lookup inputs
  const [authCode, setAuthCode] = useState("");

  // Results
  const [verificationResult, setVerificationResult] = useState(null);
  const [lookupResult, setLookupResult] = useState(null);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleVerify = async () => {
    if (!imageFile) {
      toast.error("Please select an image to verify");
      return;
    }

    setVerifying(true);
    setVerificationResult(null);

    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(",")[1];
        
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${API}/selleverify/verify`,
          {
            image_base64: base64,
            product_name: productName,
            product_description: productDescription
          },
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        
        setVerificationResult(response.data);
        toast.success("Verification complete!");
        setVerifying(false);
      };
      reader.readAsDataURL(imageFile);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
      setVerifying(false);
    }
  };

  const handleLookup = async () => {
    if (!authCode.trim()) {
      toast.error("Enter an authentication code");
      return;
    }

    setLookingUp(true);
    setLookupResult(null);

    try {
      const response = await axios.get(`${API}/selleverify/lookup/${authCode}`);
      setLookupResult(response.data);
      toast.success("Verification found!");
    } catch (error) {
      toast.error("Verification not found");
    } finally {
      setLookingUp(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getScoreColor = (score) => {
    if (score >= 90) return "text-accent-success";
    if (score >= 75) return "text-primary";
    if (score >= 50) return "text-accent-warning";
    return "text-accent-error";
  };

  const getScoreBg = (score) => {
    if (score >= 90) return "bg-accent-success/20";
    if (score >= 75) return "bg-primary/20";
    if (score >= 50) return "bg-accent-warning/20";
    return "bg-accent-error/20";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-text-secondary hover:text-text-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-heading font-bold text-text-primary">SelleVerify</span>
          </div>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent-neon flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-text-primary mb-2">
              SelleVerify Product Authentication
            </h1>
            <p className="text-text-secondary text-sm max-w-md mx-auto">
              Verify product authenticity with SellePrint fingerprinting, blockchain verification, and internet source detection
            </p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center gap-2">
            <Button
              variant={activeTab === "verify" ? "default" : "outline"}
              onClick={() => setActiveTab("verify")}
              className={activeTab === "verify" ? "bg-primary text-white" : "border-border"}
            >
              <Fingerprint className="w-4 h-4 mr-2" />
              Verify Product
            </Button>
            <Button
              variant={activeTab === "lookup" ? "default" : "outline"}
              onClick={() => setActiveTab("lookup")}
              className={activeTab === "lookup" ? "bg-primary text-white" : "border-border"}
            >
              <Search className="w-4 h-4 mr-2" />
              Lookup Code
            </Button>
            <Button
              variant={activeTab === "info" ? "default" : "outline"}
              onClick={() => setActiveTab("info")}
              className={activeTab === "info" ? "bg-primary text-white" : "border-border"}
            >
              <Info className="w-4 h-4 mr-2" />
              How It Works
            </Button>
          </div>

          {/* Verify Tab */}
          {activeTab === "verify" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6 space-y-4">
                  {/* Image Upload */}
                  <div>
                    <Label className="text-text-primary mb-2 block">Product Image</Label>
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                        imagePreview ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                      }`}
                    >
                      {imagePreview ? (
                        <div className="space-y-3">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg"
                          />
                          <p className="text-sm text-text-muted">Click to change image</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Camera className="w-10 h-10 text-text-muted mx-auto" />
                          <p className="text-text-primary">Click to upload product image</p>
                          <p className="text-xs text-text-muted">PNG, JPG up to 10MB</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </div>

                  {/* Product Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-text-muted text-xs mb-1 block">Product Name (optional)</Label>
                      <Input
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        placeholder="e.g., Nike Air Max 90"
                        className="bg-background border-border text-text-primary"
                      />
                    </div>
                    <div>
                      <Label className="text-text-muted text-xs mb-1 block">Description (optional)</Label>
                      <Input
                        value={productDescription}
                        onChange={(e) => setProductDescription(e.target.value)}
                        placeholder="Brief description"
                        className="bg-background border-border text-text-primary"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={handleVerify}
                    disabled={verifying || !imageFile}
                    className="w-full bg-gradient-to-r from-primary to-accent-neon text-white py-6"
                  >
                    {verifying ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Verifying...</>
                    ) : (
                      <><Shield className="w-5 h-5 mr-2" />Verify with SellePrint</>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Verification Result */}
              {verificationResult && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <Card className={`border-2 ${
                    verificationResult.selleprint.is_original 
                      ? "border-accent-success bg-accent-success/5" 
                      : "border-accent-warning bg-accent-warning/5"
                  }`}>
                    <CardContent className="p-6 space-y-6">
                      {/* Status Header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {verificationResult.selleprint.is_original ? (
                            <div className="w-12 h-12 rounded-full bg-accent-success/20 flex items-center justify-center">
                              <CheckCircle className="w-6 h-6 text-accent-success" />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-accent-warning/20 flex items-center justify-center">
                              <AlertTriangle className="w-6 h-6 text-accent-warning" />
                            </div>
                          )}
                          <div>
                            <h3 className="font-bold text-text-primary">
                              {verificationResult.disclosure.message}
                            </h3>
                            <p className="text-sm text-text-muted">
                              Confidence: {verificationResult.selleprint.confidence_level}
                            </p>
                          </div>
                        </div>
                        <div className={`text-3xl font-bold ${getScoreColor(verificationResult.selleprint.verification_score)}`}>
                          {verificationResult.selleprint.verification_score}
                        </div>
                      </div>

                      {/* SellePrint Details */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-background border border-border">
                          <div className="flex items-center gap-2 mb-2">
                            <Fingerprint className="w-4 h-4 text-primary" />
                            <span className="text-xs text-text-muted">SellePrint ID</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <code className="text-sm text-text-primary font-mono">
                              {verificationResult.selleprint.selleprint_id}
                            </code>
                            <button
                              onClick={() => copyToClipboard(verificationResult.selleprint.selleprint_id)}
                              className="text-text-muted hover:text-primary"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </div>

                        <div className="p-4 rounded-lg bg-background border border-border">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-4 h-4 text-primary" />
                            <span className="text-xs text-text-muted">Auth Code</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <code className="text-sm text-primary font-mono font-bold">
                              {verificationResult.selleprint.auth_code}
                            </code>
                            <button
                              onClick={() => copyToClipboard(verificationResult.selleprint.auth_code)}
                              className="text-text-muted hover:text-primary"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Blockchain */}
                      <div className="p-4 rounded-lg bg-background border border-border">
                        <div className="flex items-center gap-2 mb-2">
                          <Link2 className="w-4 h-4 text-primary" />
                          <span className="text-xs text-text-muted">Blockchain Transaction</span>
                          {verificationResult.blockchain_verified && (
                            <span className="px-2 py-0.5 rounded bg-accent-success/20 text-accent-success text-xs">
                              Verified
                            </span>
                          )}
                        </div>
                        <code className="text-xs text-text-primary font-mono break-all">
                          {verificationResult.selleprint.blockchain_tx}
                        </code>
                      </div>

                      {/* Internet Matches Warning */}
                      {verificationResult.internet_source_detected && (
                        <div className="p-4 rounded-lg bg-accent-warning/10 border border-accent-warning/30">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-4 h-4 text-accent-warning" />
                            <span className="text-sm font-medium text-accent-warning">
                              Internet Source Detected
                            </span>
                          </div>
                          <p className="text-xs text-text-muted mb-2">
                            {verificationResult.selleprint.internet_matches} match(es) found online
                          </p>
                          {verificationResult.source_urls.length > 0 && (
                            <div className="space-y-1">
                              {verificationResult.source_urls.map((url, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                  <ExternalLink className="w-3 h-3 text-text-muted" />
                                  <span className="text-text-muted truncate">{url}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Fingerprint Hash */}
                      <div className="text-center pt-4 border-t border-border">
                        <p className="text-xs text-text-muted mb-1">Fingerprint Hash</p>
                        <code className="text-xs text-text-muted font-mono">
                          {verificationResult.selleprint.fingerprint_hash}
                        </code>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Lookup Tab */}
          {activeTab === "lookup" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6 space-y-4">
                  <div>
                    <Label className="text-text-primary mb-2 block">Authentication Code</Label>
                    <Input
                      value={authCode}
                      onChange={(e) => setAuthCode(e.target.value.toUpperCase())}
                      placeholder="e.g., SV-A1B2C3D4-E5F6G7H8"
                      className="bg-background border-border text-text-primary font-mono text-lg"
                    />
                  </div>
                  <Button
                    onClick={handleLookup}
                    disabled={lookingUp || !authCode.trim()}
                    className="w-full bg-primary hover:bg-primary-hover text-white py-6"
                  >
                    {lookingUp ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Looking up...</>
                    ) : (
                      <><Search className="w-5 h-5 mr-2" />Lookup Verification</>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {lookupResult && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <Card className="bg-background-paper border-border">
                    <CardContent className="p-6 space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-accent-success/20 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-accent-success" />
                        </div>
                        <div>
                          <h3 className="font-bold text-text-primary">Verification Found</h3>
                          <p className="text-xs text-text-muted">
                            Created: {new Date(lookupResult.verification.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-text-muted">SellePrint ID:</span>
                          <p className="font-mono text-text-primary">{lookupResult.verification.selleprint_id}</p>
                        </div>
                        <div>
                          <span className="text-text-muted">Verification Score:</span>
                          <p className={`font-bold ${getScoreColor(lookupResult.verification.verification_score)}`}>
                            {lookupResult.verification.verification_score}/100
                          </p>
                        </div>
                        <div>
                          <span className="text-text-muted">Product:</span>
                          <p className="text-text-primary">{lookupResult.verification.product_name || "Not specified"}</p>
                        </div>
                        <div>
                          <span className="text-text-muted">Internet Source:</span>
                          <p className={lookupResult.verification.internet_source_detected ? "text-accent-warning" : "text-accent-success"}>
                            {lookupResult.verification.internet_source_detected ? "Detected" : "Original"}
                          </p>
                        </div>
                      </div>

                      {lookupResult.blockchain_record && (
                        <div className="p-3 rounded-lg bg-background border border-border">
                          <div className="flex items-center gap-2 mb-2">
                            <Link2 className="w-4 h-4 text-primary" />
                            <span className="text-xs text-text-muted">Blockchain Record</span>
                          </div>
                          <code className="text-xs text-text-muted font-mono break-all">
                            {lookupResult.blockchain_record.tx_hash}
                          </code>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Info Tab */}
          {activeTab === "info" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6 space-y-6">
                  <h3 className="font-heading font-bold text-lg text-text-primary">
                    How SelleVerify Works
                  </h3>

                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Fingerprint className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-text-primary">SellePrint Fingerprinting</h4>
                        <p className="text-sm text-text-muted">
                          Creates a unique digital fingerprint of your product image using advanced pattern recognition technology.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent-warning/10 flex items-center justify-center flex-shrink-0">
                        <Search className="w-5 h-5 text-accent-warning" />
                      </div>
                      <div>
                        <h4 className="font-medium text-text-primary">Internet Source Detection</h4>
                        <p className="text-sm text-text-muted">
                          Compares your image against millions of internet photos to detect stock images or stolen photos. Discloses if an image was taken from the internet.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent-neon/10 flex items-center justify-center flex-shrink-0">
                        <Link2 className="w-5 h-5 text-accent-neon" />
                      </div>
                      <div>
                        <h4 className="font-medium text-text-primary">Blockchain Verification</h4>
                        <p className="text-sm text-text-muted">
                          Records verification on an immutable blockchain ledger. Each verification gets a unique transaction hash that can be independently verified.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent-success/10 flex items-center justify-center flex-shrink-0">
                        <Store className="w-5 h-5 text-accent-success" />
                      </div>
                      <div>
                        <h4 className="font-medium text-text-primary">Verified Merchant</h4>
                        <p className="text-sm text-text-muted">
                          Merchants can register for verification status, building trust with buyers through a merchant trust score.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-text-primary">Authentication Code</h4>
                        <p className="text-sm text-text-muted">
                          Each verified product receives a unique authentication code that buyers can use to verify authenticity before purchasing.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-background border border-border">
                    <h4 className="font-medium text-text-primary mb-3">Verification Scores</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-20 px-2 py-1 rounded bg-accent-success/20 text-accent-success text-center text-xs">90-100</span>
                        <span className="text-text-muted">Excellent - Highly authentic, original photo</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-20 px-2 py-1 rounded bg-primary/20 text-primary text-center text-xs">75-89</span>
                        <span className="text-text-muted">Good - Authentic with minor concerns</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-20 px-2 py-1 rounded bg-accent-warning/20 text-accent-warning text-center text-xs">50-74</span>
                        <span className="text-text-muted">Moderate - Some verification issues</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-20 px-2 py-1 rounded bg-accent-error/20 text-accent-error text-center text-xs">0-49</span>
                        <span className="text-text-muted">Low - Significant authenticity concerns</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
